import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ensurePolygon,
    ensureUsdcAllowance,
    mintOnContract,
    getUsdcBalance,
} from "../lib/web3";
import { useWallet } from "../lib/wallet";
import { submitMint } from "../lib/api";
import { X, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const CONTRACT_ADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS;
const POLYGONSCAN = process.env.REACT_APP_EXPLORER_TX_URL || "https://polygonscan.com/tx/";

export default function MintModal({ open, onClose, token, status, onSuccess }) {
    const { t } = useTranslation();
    const { account, connect } = useWallet();
    const [phase, setPhase] = useState("idle"); // idle | approving | minting | recording | done
    const [approveTx, setApproveTx] = useState(null);
    const [mintTx, setMintTx] = useState(null);

    if (!open || !token) return null;

    const handleMint = async () => {
        if (!CONTRACT_ADDRESS) {
            toast.error("Contract address not configured");
            return;
        }
        try {
            if (!account) { await connect(); return; }
            await ensurePolygon();

            // Pre-flight: check USDC balance for clearer error than MetaMask's revert
            const bal = await getUsdcBalance({
                usdcAddress: status.usdc_address,
                address: account,
            });
            if (bal < token.price) {
                toast.error(t("mint.insufficient_usdc", { needed: token.price, have: bal.toFixed(2) }));
                return;
            }

            // 1) Approve USDC to the contract (skipped if allowance already sufficient)
            setPhase("approving");
            const aTx = await ensureUsdcAllowance({
                usdcAddress: status.usdc_address,
                spender: CONTRACT_ADDRESS,
                ownerAddress: account,
                requiredUsd: token.price,
            });
            if (aTx) setApproveTx(aTx);

            // 2) Call mint(tokenIds) on the NFT contract — pulls USDC and mints
            setPhase("minting");
            const mTx = await mintOnContract({
                contractAddress: CONTRACT_ADDRESS,
                tokenIds: [token.number],
            });
            setMintTx(mTx);

            // 3) Notify backend so the parcel becomes "sold" in our DB
            setPhase("recording");
            await submitMint({
                token_numbers: [token.number],
                wallet: account,
                tx_hash: mTx,
                total_paid: token.price,
            });
            setPhase("done");
            toast.success(t("mint.success", { n: token.number }));
            if (onSuccess) onSuccess();
        } catch (e) {
            console.error(e);
            const msg = e?.shortMessage || e?.reason || e?.message || t("mint.tx_failed");
            toast.error(msg.length > 120 ? msg.slice(0, 120) + "…" : msg);
            setPhase("idle");
        }
    };

    const busy = phase !== "idle" && phase !== "done";

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-obsidian/80 backdrop-blur-sm">
            <div className="relative w-full max-w-md bg-slate-950 border border-white/10" data-testid="mint-modal">
                <button data-testid="mint-modal-close" onClick={onClose}
                    className="absolute top-3 right-3 text-slate-400 hover:text-white">
                    <X className="w-5 h-5" />
                </button>
                <div className="p-6 border-b border-white/10">
                    <div className="text-xs uppercase tracking-[0.2em] text-frost">{t("mint.title")}</div>
                    <div className="font-serif text-3xl mt-2">{t("detail.parcel")} #{token.number}</div>
                    <div className="font-mono text-xs text-slate-400 mt-1">
                        {token.lat.toFixed(4)}°{token.lat < 0 ? "S" : "N"}, {Math.abs(token.lon).toFixed(4)}°{token.lon < 0 ? "W" : "E"}
                    </div>
                </div>
                <div className="p-6 space-y-3">
                    <Row label={t("mint.phase")} value={token.phase.replace("_", " ").toUpperCase()} />
                    <Row label={t("mint.price")} value={`${token.price.toLocaleString()} USDC`} accent />
                    <Row label={t("mint.network")} value={status?.chain_name || "Polygon"} />
                    <Row label={t("mint.contract")} value={truncate(CONTRACT_ADDRESS)} mono />
                    <Row label={t("mint.receiver")} value={truncate(status?.receiver_wallet)} mono />
                    {approveTx && <TxRow label={t("mint.approve_tx")} hash={approveTx} />}
                    {mintTx && <TxRow label={t("mint.mint_tx")} hash={mintTx} />}
                </div>
                <div className="p-6 border-t border-white/10 flex gap-3">
                    <button data-testid="mint-modal-cancel" onClick={onClose}
                        disabled={busy}
                        className="flex-1 btn-sharp border border-white/15 px-4 py-3 text-xs hover:border-white/40 disabled:opacity-40">
                        {t("mint.cancel")}
                    </button>
                    {phase === "done" ? (
                        <button data-testid="mint-modal-done" onClick={onClose}
                            className="flex-1 btn-sharp bg-frost text-obsidian px-4 py-3 text-xs">
                            {t("mint.close")}
                        </button>
                    ) : (
                        <button
                            data-testid="mint-modal-confirm"
                            onClick={handleMint}
                            disabled={busy}
                            className="flex-1 btn-sharp bg-white text-obsidian px-4 py-3 text-xs hover:bg-slate-200 disabled:opacity-60 inline-flex items-center justify-center gap-2"
                        >
                            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            {phase === "idle" && (account ? t("mint.pay", { price: token.price.toLocaleString() }) : t("mint.connect"))}
                            {phase === "approving" && t("mint.approving")}
                            {phase === "minting" && t("mint.minting")}
                            {phase === "recording" && t("mint.recording")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, accent, mono }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</span>
            <span className={`${mono ? "font-mono" : ""} ${accent ? "text-frost text-base" : "text-white"}`}>
                {value}
            </span>
        </div>
    );
}

function TxRow({ label, hash }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</span>
            <a
                href={`${POLYGONSCAN}${hash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-frost hover:underline inline-flex items-center gap-1"
                data-testid={`mint-${label.toLowerCase().replace(/\s+/g, "-")}-link`}
            >
                {truncate(hash)} <ExternalLink className="w-3 h-3" />
            </a>
        </div>
    );
}

function truncate(a) {
    if (!a) return "—";
    return `${a.slice(0, 6)}…${a.slice(-4)}`;
}
